import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useProjects, useShowcaseProjects, useCompanyInfo, usePageContent } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function ProjekterPage() {
  const [activeFilter, setActiveFilter] = useState('Alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const projects = useProjects();
  const curatedShowcaseProjects = useShowcaseProjects();
  const companyInfo = useCompanyInfo();
  const page = usePageContent('projekter', {
    badge: "Dokumenteret kvalitet",
    heading: "Projekter & Referencer",
    text: "Få et indblik i nogle af de hegn, porte, carporte og uderumsløsninger, vi har skabt for vores kunder. Vi leverer og monterer i hele Danmark.",
    metaTitle: "Projekter | HOVENDALS ApS | Referencer i hele Danmark",
    metaDescription: "Se udvalgte projekter inden for hegn, låger, porte, carporte og terrasser udført for kunder i hele Danmark.",
  });

  // Combine curated new projects with existing projects
  const allProjectsList = [
    ...curatedShowcaseProjects.map((p, i) => ({
      id: `curated-${i}`,
      title: p.title,
      category: p.category.includes('Erhverv') ? 'Erhverv' : 'Privat',
      subCategory: p.category,
      mainImage: p.image,
      desc: p.desc,
      path: p.link,
    })),
    ...projects.map((p) => ({
      id: p.slug,
      title: p.title,
      category: p.category,
      subCategory: p.category,
      mainImage: p.mainImage,
      desc: p.description?.slice(0, 90) + '...',
      path: p.path,
    })),
  ];

  const filteredProjects = activeFilter === 'Alle'
    ? allProjectsList
    : allProjectsList.filter((p) => p.category.toLowerCase() === activeFilter.toLowerCase());

  return (
    <>
      <SEO
        title={page.metaTitle || "Projekter | HOVENDALS ApS | Referencer i hele Danmark"}
        description={page.metaDescription || "Se udvalgte projekter inden for hegn, låger, porte, carporte og terrasser udført for kunder i hele Danmark."}
        ogImage="/_assets/komposithegn-laage.webp"
      />

      {/* Hero */}
      <section className="relative py-24 md:py-32 bg-[#F3F4F7] border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              {page.badge || "Dokumenteret kvalitet"}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#143463] tracking-tight leading-[1.1]">
              {page.heading || "Projekter & Referencer"}
            </h1>
            <p className="text-base sm:text-lg text-[#4B515D] font-light leading-relaxed">
              {page.text || "Få et indblik i nogle af de hegn, porte, carporte og uderumsløsninger, vi har skabt for vores kunder. Vi leverer og monterer i hele Danmark."}
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 pb-6 border-b border-[#F3F4F7]">
            <div>
              <h2 className="text-2xl font-light text-[#143463]">
                Udført arbejde i praksis
              </h2>
              <p className="text-xs text-[#4B515D] font-light mt-1">
                Viser {filteredProjects.length} udvalgte projekter
              </p>
            </div>

            <div className="flex items-center gap-2">
              {['Alle', 'Privat', 'Erhverv'].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveFilter(cat)}
                  className={`px-5 py-2 text-xs uppercase tracking-wider font-medium transition-colors cursor-pointer ${
                    activeFilter === cat
                      ? 'bg-[#004FA4] text-white'
                      : 'bg-[#F3F4F7] text-[#143463] hover:bg-[#D6D3CE]/60'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* 10.1 Projektkort: Store billeder, ensartede forhold (4:3), kort titel, diskret hover */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProjects.map((item, idx) => (
              <article
                key={item.id}
                className="bg-white border border-[#D6D3CE]/60 overflow-hidden flex flex-col group transition-all duration-300 hover:border-[#004FA4]"
              >
                <div
                  onClick={() => setLightboxIndex(idx)}
                  className="relative aspect-[4/3] overflow-hidden bg-[#F3F4F7] block cursor-pointer"
                >
                  <img
                    src={imageUrl(item.mainImage, "width=800,quality=85,format=auto")}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 text-[11px] font-medium tracking-wider uppercase bg-[#143463]/90 text-white">
                      {item.subCategory || item.category}
                    </span>
                  </div>
                </div>

                <div className="p-5 flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-light text-[#143463] group-hover:text-[#004FA4] transition-colors leading-snug">
                      <Link to={item.path}>{item.title}</Link>
                    </h3>
                    {item.desc && (
                      <p className="text-xs text-[#4B515D] font-light mt-2 line-clamp-2 leading-relaxed">
                        {item.desc}
                      </p>
                    )}
                  </div>

                  <div className="pt-4 mt-4 border-t border-[#F3F4F7] flex items-center justify-between">
                    <Link
                      to={item.path}
                      className="text-xs font-medium uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <span>Læs mere</span>
                      <i className="fa-sharp fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                    </Link>
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      className="text-xs text-[#4B515D] hover:text-[#004FA4] transition-colors"
                      aria-label="Forstør billede"
                    >
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* Slogan og CTA banner */}
      <section className="py-16 bg-[#F3F4F7] border-t border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#004FA4]">
            {companyInfo.slogan}
          </p>
          <h2 className="text-2xl sm:text-3xl font-light text-[#143463]">
            Har du et specifikt projekt i tankerne?
          </h2>
          <p className="text-sm text-[#4B515D] font-light max-w-xl mx-auto">
            Vi rådgiver gerne om muligheder, opmåling og materialer tilpasset dit budget og dine omgivelser.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
            >
              <span>Indhent uforpligtende tilbud</span>
              <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      <QuoteSection />

      {lightboxIndex !== null && (
        <LightboxModal
          images={filteredProjects.map((p) => ({ src: p.mainImage, title: p.title }))}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}
